// Routes.js
import React, { Suspense } from "react";
import { Switch, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PaymentReceipt,
  PaymentsList,
  PaymentPage,
  Invoice,
  Invoices,
  InvoiceDetails,
  ClientList,
  Login,
  ProtectedDashboard,
  Settings,
  SettingsHome,
  ZatcaIntegrationSettings,
  ProfileSettings,
  Forgot,
  Reset,
  AddCategory,
  Categories,
  Items,
  AddItem,
  BarcodeSettings,
  POS,
  LegacyPOS,
  ModernPOSTerminal,
  Receipt,
  // Journal components
  JournalsList,
  JournalDetails,
  JournalEntryDetailsPage,
  TrialBalanceReport,
  JournalReport,
  Download,
  ProfitCentersList,
  UserGroups,
  AddUserGroup,
  Users,
  EmployeeList,
  RentalList,
  RentalForm,
  RentalHome,
  BuildingsList,
  BuildingDetails,
  Accounts,
  Transaction,
  Transactions,
  TaxSettings,
  AccountingHome,
  AccountingReportsPage,
  HRHomePage,
  Letter,
  LettersHome,
  LetterViewer,
  LettersList,
  TemplateManager,
  TemplateLibrary,
  ApprovalDashboard,
  ApprovalQueue,
  LetterInbox,
  LetterOutbox,
  Costcenters,
  TrialBalance,
  BalanceSheet,
  IncomeStatement,
  CashFlow,
  ComprehensiveIncome,
  StockholdersEquity,
  GeneralLedger,
  AgingReport,
  AccountStatement,
  VATReport,
  ProjectProfitability,
  PurchaseInvoice,
  PurchaseInvoiceDetails,
  PurchaseInvoices,
  GeneratePayroll,
  PayrollList,
  PayrollsHome,
  BulkGeneratePayroll,
  PayrollAnalytics,
  PayrollPayments,
  AssetsList,
  AssetsForm,
  AttendanceList,
  AttendanceReports,
  TimeTracking,
  RecordAttendance,
  AttendanceHome,
  ModernCheckInOut,
  ShiftManagement,
  HolidayManagement,
  AttendanceSettings,
  SMSProvider,
  CommunicationCenter,
  BarcodePrint,
  AppHomePage,
  QRCodeGenerator,
  QRCodeForm,
  PublicQRCode,
  ProjectsList,
  AccountingSettings,
  ConstructionProjectsList,
  ConstructionProjectForm,
  ConstructionProjectDashboard,
  BOQList,
  BOQForm,
  ProgressBillingList,
  SitesList,
  DailyReportsList,
  SubcontractorsList,
  MaterialRequestsList,
  ConstructionReports,
  ProjectBudget,
  ProjectExpenses,
  FinancialReport,
  ProgressReport,
  ScheduleReport,
  ResourceReport,
  MaterialReport,
  OverallAnalytics,
  // Law Firm components
  LawFirmDashboard,
  CaseList,
  CaseDetail,
  CaseForm,
  TimeEntryList,
  TimeEntryForm,
  TaskList,
  TaskForm,
  BillingDashboard,
  ExpenseList,
  ExpenseForm,
  ReportsDashboard,
  LawFirmFinancialReport,
  TimeUtilizationReport,
  AttorneyPerformanceReport,
  TrustAccountDashboard,
  DocumentManagement,
  MatterManagement,
  CourtCalendar,
  ConflictChecker,
  ClientPortal,
  ClientPortalHome,
  ClientProfile,
  ClientLogin,
  ClientBillingReport,
  CaseProfitabilityReport,
  PracticeAreaReport,
  RealizationRateReport,
  CustomReport,
  NotificationsList,
  Vouchers,
  Coupons,
  CreateCoupons,
  Properties,
  AddProperties,
  ClinicManagementHome,
  DoctorList,
  DoctorForm,
  DoctorDetail,
  PatientList,
  PatientForm,
  PatientDetail,
  AppointmentList,
  AppointmentForm,
  AppointmentDetail,
  LabResultList,
  LabResultForm,
  LabResultDetail,
  LabTestList,
  LabTestForm,
  LabTestDetail,
  MedicalRecordList,
  MedicalRecordForm,
  MedicalRecordDetail,
  PrescriptionList,
  PrescriptionForm,
  PrescriptionDetail,
  MedicationList,
  MedicationForm,
  MedicationDetail,
  Messaging,
  Inventory,
  InventoryHome,
  InventoryDetail,
  ManufacturingHome,
  ManufacturingBoms,
  ManufacturingProductionPlans,
  ManufacturingRoutings,
  ManufacturingSettings,
  ManufacturingQuality,
  ManufacturingSubcontracting,
  ManufacturingMaintenance,
  ManufacturingReports,
  ManufacturingWorkCenters,
  ManufacturingWorkOrders,
  ExcelImportExportPage,
  ExcelWorkbooksPage,
  WarehouseList,
  UnitOfMeasureList,
  InventoryAdjustments,
  AddInventoryAdjustment,
  InventoryTransactions,
  InventoryTransfer,
  InventoryTransfers,
  InventoryAlerts,
  InventoryReports,
  InventorySearch,
  InventorySettings,
  Units,
  Poslist,
  AssetsViewer,
  EditProperties,
  viewProperties,
  MySubscription,
  MyBilling,
  TodoApp,
  CreditDebitNoteList,
  InventoryOrders,
  AddCreditDebitNote,
  CreditDebitNoteDetails,
  WorkflowSettings,
  WorkflowInbox,
} from "./lazyComponents";

import LawFirmClientPortal from "./components/ClientPortal/LawFirmClientPortal";
import ConstructionClientPortal from "./components/ClientPortal/ConstructionClientPortal";
import HealthcareClientPortal from "./components/ClientPortal/HealthcareClientPortal";
import AccountingClientPortal from "./components/ClientPortal/AccountingClientPortal";
import ClientPortalSettings from "./components/ClientPortal/ClientPortalSettings";
import AISettings from "./components/AISettings/AISettings";
import MFASettings from "./components/Settings/MFASettings";
import SecuritySettings from "./components/Settings/SecuritySettings";
import ModuleSettings from "./components/Settings/ModuleSettings";
import WebsiteManager from "./components/Website/WebsiteManager";
const SiteWrapper = React.lazy(() => import("./site/SiteWrapper"));

export default function Routes() {
  const { t } = useTranslation("operations");

  return (
    <Suspense fallback={<div>{t("Loading...")}</div>}>
      <Switch>
        {/* Public e-commerce website — no lang prefix, no auth */}
        <Route path="/w/:slug/products" component={SiteWrapper} />
        <Route path="/w/:slug/cart"     component={SiteWrapper} />
        <Route path="/w/:slug/contact"  component={SiteWrapper} />
        <Route path="/w/:slug"          component={SiteWrapper} />
        {/* Public Routes */}
        <Route path="/qr/:link" exact component={PublicQRCode} />
        <Route path="/:lang/qr/:link" exact component={PublicQRCode} />
        <Route path="/:lang?/" exact component={Login} />
        <Route path="/login" exact component={Login} />
        <Route path="/:lang/login" exact component={Login} />
        <Route path="/:lang/app/main" exact component={AppHomePage} />
        <Route
          path="/:lang/app/dashboard"
          exact
          component={ProtectedDashboard}
        />
        <Route path="/:lang/app/settings" exact component={SettingsHome} />
        <Route path="/:lang/app/settings/general" exact component={Settings} />
        <Route
          path="/:lang/app/settings/zatca"
          exact
          component={ZatcaIntegrationSettings}
        />
        <Route
          path="/:lang/app/settings/tax-settings"
          exact
          component={TaxSettings}
        />
        <Route
          path="/:lang/app/settings/pos-settings"
          exact
          component={Poslist}
        />
        <Route
          path="/:lang/app/properties/add"
          exact
          component={AddProperties}
        />
        <Route path="/:lang/app/properties" exact component={Properties} />
        <Route
          path="/:lang/app/properties/edit/:id"
          exact
          component={EditProperties}
        />
        <Route
          path="/:lang/app/properties/view/:id"
          exact
          component={viewProperties}
        />
        {/* Payments Routes */}
        <Route path="/:lang/app/payments" exact component={PaymentsList} />
        <Route
          path="/:lang/app/payments/:pageNumber"
          exact
          component={PaymentsList}
        />
        <Route
          path="/:lang/app/payments/client/:clientId"
          exact
          component={PaymentsList}
        />
        {/* Invoices Routes */}
        <Route path="/:lang/app/invoices" exact component={Invoices} />
        <Route
          path="/:lang/app/invoices/:pageNumber"
          exact
          component={Invoices}
        />
        <Route
          path="/:lang/app/invoices/client/:clientId"
          exact
          component={Invoices}
        />
        {/* Journal Routes */}
        <Route path="/:lang/app/journals" exact component={JournalsList} />
        <Route
          path="/:lang/app/journals/reports/trial-balance"
          exact
          component={TrialBalanceReport}
        />
        <Route
          path="/:lang/app/journals/reports/journal"
          exact
          component={JournalReport}
        />
        <Route
          path="/:lang/app/journal-entries/:id"
          exact
          component={JournalEntryDetailsPage}
        />
        <Route
          path="/:lang/app/journals/:id"
          exact
          component={JournalDetails}
        />
        <Route path="/:lang/app/profile" exact component={ProfileSettings} />
        <Route path="/:lang/forgot" exact component={Forgot} />
        <Route path="/:lang/reset/:token" exact component={Reset} />
        <Route path="/:lang/payment" exact component={PaymentPage} />
        <Route path="/:lang/download" exact component={Download} />
        <Route path="/:lang/app/add/invoice" exact component={Invoice} />
        <Route path="/:lang/app/edit/invoice/:id" exact component={Invoice} />
        <Route
          path="/:lang/app/duplicate/invoice/:id"
          exact
          component={Invoice}
        />
        <Route path="/:lang/app/invoice/:id" exact component={InvoiceDetails} />
        <Route path="/:lang/app/invoices" exact component={Invoices} />
        <Route
          path="/:lang/app/invoices/byRental/:rentalId"
          exact
          component={Invoices}
        />
        <Route path="/:lang/app/pos" exact component={POS} />
        <Route path="/:lang/app/pos/legacy" exact component={LegacyPOS} />
        <Route
          path="/:lang/app/pos/terminal/:terminalId"
          exact
          component={ModernPOSTerminal}
        />
        <Route path="/:lang/app/pos/:id" exact component={Receipt} />
        <Route path="/:lang/app/pos/receipt/:id" exact component={Receipt} />
        <Route path="/:lang/app/payment/:id" exact component={PaymentReceipt} />
        <Route path="/:lang/app/customers" exact component={ClientList} />
        <Route
          path="/:lang/app/inventory/warehouses"
          exact
          component={WarehouseList}
        />
        <Route
          path="/:lang/app/inventory/products/categories/add"
          exact
          component={AddCategory}
        />
        <Route
          path="/:lang/app/inventory/products/categories"
          exact
          component={Categories}
        />
        <Route path="/:lang/app/inventory/products" exact component={Items} />
        <Route
          path="/:lang/app/inventory/products/add"
          exact
          component={AddItem}
        />
        <Route
          path="/:lang/app/inventory/products/edit/:id"
          exact
          component={AddItem}
        />
        <Route path="/:lang/app/inventory" exact component={InventoryHome} />
        <Route
          path="/:lang/app/manufacturing"
          exact
          component={ManufacturingHome}
        />
        <Route
          path="/:lang/app/manufacturing/settings"
          exact
          component={ManufacturingSettings}
        />
        <Route
          path="/:lang/app/manufacturing/boms"
          exact
          component={ManufacturingBoms}
        />
        <Route
          path="/:lang/app/manufacturing/boms/:id"
          exact
          component={ManufacturingBoms}
        />
        <Route
          path="/:lang/app/manufacturing/work-centers"
          exact
          component={ManufacturingWorkCenters}
        />
        <Route
          path="/:lang/app/manufacturing/work-centers/:id"
          exact
          component={ManufacturingWorkCenters}
        />
        <Route
          path="/:lang/app/manufacturing/routings"
          exact
          component={ManufacturingRoutings}
        />
        <Route
          path="/:lang/app/manufacturing/routings/:id"
          exact
          component={ManufacturingRoutings}
        />
        <Route
          path="/:lang/app/manufacturing/plans"
          exact
          component={ManufacturingProductionPlans}
        />
        <Route
          path="/:lang/app/manufacturing/plans/:id"
          exact
          component={ManufacturingProductionPlans}
        />
        <Route
          path="/:lang/app/manufacturing/work-orders"
          exact
          component={ManufacturingWorkOrders}
        />
        <Route
          path="/:lang/app/manufacturing/work-orders/:id"
          exact
          component={ManufacturingWorkOrders}
        />
        <Route
          path="/:lang/app/manufacturing/quality"
          exact
          component={ManufacturingQuality}
        />
        <Route
          path="/:lang/app/manufacturing/quality/capas/:id"
          exact
          component={ManufacturingQuality}
        />
        <Route
          path="/:lang/app/manufacturing/subcontracting"
          exact
          component={ManufacturingSubcontracting}
        />
        <Route
          path="/:lang/app/manufacturing/subcontracting/:id"
          exact
          component={ManufacturingSubcontracting}
        />
        <Route
          path="/:lang/app/manufacturing/maintenance"
          exact
          component={ManufacturingMaintenance}
        />
        <Route
          path="/:lang/app/manufacturing/maintenance/:id"
          exact
          component={ManufacturingMaintenance}
        />
        <Route
          path="/:lang/app/manufacturing/reports"
          exact
          component={ManufacturingReports}
        />
        <Route
          path="/:lang/app/inventory/home"
          exact
          component={InventoryHome}
        />
        <Route path="/:lang/app/inventory/items" exact component={Inventory} />
        <Route
          path="/:lang/app/inventory/items/add"
          exact
          component={AddItem}
        />
        <Route
          path="/:lang/app/inventory/items/edit/:id"
          exact
          component={AddItem}
        />
        <Route
          path="/:lang/app/inventory/warehouse"
          exact
          component={WarehouseList}
        />
        <Route
          path="/:lang/app/inventory/unit-of-measure"
          exact
          component={UnitOfMeasureList}
        />
        <Route
          path="/:lang/app/inventory/adjustments"
          exact
          component={InventoryAdjustments}
        />
        <Route
          path="/:lang/app/inventory/adjustments/add"
          exact
          component={AddInventoryAdjustment}
        />
        <Route
          path="/:lang/app/inventory/adjustments/edit/:id"
          exact
          component={AddInventoryAdjustment}
        />
        <Route
          path="/:lang/app/inventory/transactions"
          exact
          component={InventoryTransactions}
        />
        <Route
          path="/:lang/app/inventory/transfers"
          exact
          component={InventoryTransfers}
        />
        <Route
          path="/:lang/app/inventory/transfer/new"
          exact
          component={InventoryTransfer}
        />
        <Route
          path="/:lang/app/inventory/alerts"
          exact
          component={InventoryAlerts}
        />
        <Route
          path="/:lang/app/inventory/reports"
          exact
          component={InventoryReports}
        />
        <Route
          path="/:lang/app/inventory/search"
          exact
          component={InventorySearch}
        />
        <Route
          path="/:lang/app/inventory/settings"
          exact
          component={InventorySettings}
        />
        <Route path="/:lang/app/inventory/units" exact component={Units} />
        <Route
          path="/:lang/app/inventory/:id"
          exact
          component={InventoryDetail}
        />
        <Route
          path="/:lang/app/excel"
          exact
          component={ExcelImportExportPage}
        />
        <Route
          path="/:lang/app/excel-workbooks"
          exact
          component={ExcelWorkbooksPage}
        />
        <Route
          path="/:lang/app/excel-workbooks/:id"
          exact
          component={ExcelWorkbooksPage}
        />
        <Route
          path="/:lang/app/barcode-settings"
          exact
          component={BarcodeSettings}
        />
        <Route path="/:lang/app/add/group" exact component={AddUserGroup} />
        <Route
          path="/:lang/app/edit/group/:id"
          exact
          component={AddUserGroup}
        />
        <Route path="/:lang/app/groups" exact component={UserGroups} />
        <Route path="/:lang/app/users" exact component={Users} />
        <Route path="/:lang/app/hr/employees" exact component={EmployeeList} />
        <Route
          path="/:lang/app/rentals/unpaid"
          exact
          render={() => <RentalList unpaid />}
        />
        <Route path="/:lang/app/rentals" exact component={RentalHome} />
        <Route path="/:lang/app/rentals/all" exact component={RentalList} />
        <Route path="/:lang/app/rentals/home" exact component={RentalHome} />
        <Route path="/:lang/app/rentals/add" exact component={RentalForm} />
        <Route
          path="/:lang/app/rentals/edit/:id"
          exact
          component={RentalForm}
        />
        {/* Rental Document Templates Routes */}
        <Route
          path="/:lang/app/rentals/doc/:type/add"
          exact
          component={Letter}
        />
        <Route
          path="/:lang/app/rentals/doc/:type/edit/:id"
          exact
          component={Letter}
        />
        <Route
          path="/:lang/app/rentals/doc/:type/view/:id"
          exact
          component={LetterViewer}
        />
        <Route
          path="/:lang/app/rentals/doc/:type"
          exact
          component={LettersList}
        />
        <Route path="/:lang/app/buildings" exact component={BuildingsList} />
        <Route
          path="/:lang/app/buildings/:id/details"
          exact
          component={BuildingDetails}
        />
        <Route path="/:lang/app/Accounts" exact component={Accounts} />
        <Route path="/:lang/app/transaction" exact component={Transaction} />
        <Route
          path="/:lang/app/transaction/edit/:id"
          exact
          component={Transaction}
        />
        <Route path="/:lang/app/transactions" exact component={Transactions} />
        <Route
          path="/:lang/app/transactions/:pageNumber"
          exact
          component={Transactions}
        />
        <Route
          path="/:lang/app/transactions/client/:clientId"
          exact
          component={Transactions}
        />
        <Route path="/:lang/app/accounting" exact component={AccountingHome} />
        <Route
          path="/:lang/app/accounting-reports"
          exact
          component={AccountingReportsPage}
        />
        <Route path="/:lang/app/hr" exact component={HRHomePage} />
        <Route path="/:lang/app/projects" exact component={ProjectsList} />
        {/* Construction Management Routes - New Structure */}
        <Route
          path="/:lang/app/construction/projects"
          exact
          component={ConstructionProjectsList}
        />
        <Route
          path="/:lang/app/construction"
          exact
          component={ConstructionProjectsList}
        />
        <Route
          path="/:lang/app/construction/projects/add"
          exact
          component={ConstructionProjectForm}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/edit"
          exact
          component={ConstructionProjectForm}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId"
          exact
          component={ConstructionProjectDashboard}
        />
        {/* BOQ Routes with projectId - More specific routes first */}
        <Route
          path="/:lang/app/construction/projects/:projectId/boq/add"
          exact
          component={BOQForm}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/boq/:id/edit"
          exact
          component={BOQForm}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/boq/:id"
          exact
          component={BOQForm}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/boq"
          exact
          component={BOQList}
        />
        {/* Progress Billing Routes with projectId - More specific routes first */}
        <Route
          path="/:lang/app/construction/projects/:projectId/progress-billing/add"
          exact
          component={ProgressBillingList}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/progress-billing/:id/edit"
          exact
          component={ProgressBillingList}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/progress-billing/:id"
          exact
          component={ProgressBillingList}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/progress-billing"
          exact
          component={ProgressBillingList}
        />
        {/* Sites Routes with projectId */}
        <Route
          path="/:lang/app/construction/projects/:projectId/sites"
          exact
          component={SitesList}
        />
        {/* Daily Reports Routes with projectId */}
        <Route
          path="/:lang/app/construction/projects/:projectId/daily-reports"
          exact
          component={DailyReportsList}
        />
        {/* Subcontractors Routes with projectId */}
        <Route
          path="/:lang/app/construction/projects/:projectId/subcontractors"
          exact
          component={SubcontractorsList}
        />
        {/* Material Requests Routes with projectId */}
        <Route
          path="/:lang/app/construction/projects/:projectId/material-requests"
          exact
          component={MaterialRequestsList}
        />
        {/* Budget Routes with projectId */}
        <Route
          path="/:lang/app/construction/projects/:projectId/budget"
          exact
          component={ProjectBudget}
        />
        {/* Expenses Routes with projectId */}
        <Route
          path="/:lang/app/construction/projects/:projectId/expenses"
          exact
          component={ProjectExpenses}
        />
        {/* Reports Routes with projectId */}
        <Route
          path="/:lang/app/construction/projects/:projectId/reports"
          exact
          component={ConstructionReports}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/reports/financial"
          exact
          component={FinancialReport}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/reports/progress"
          exact
          component={ProgressReport}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/reports/schedule"
          exact
          component={ScheduleReport}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/reports/resource"
          exact
          component={ResourceReport}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/reports/material"
          exact
          component={MaterialReport}
        />
        <Route
          path="/:lang/app/construction/projects/:projectId/reports/analytics"
          exact
          component={OverallAnalytics}
        />
        {/* Law Firm Management Routes */}
        <Route
          path="/:lang/app/law-firm/dashboard"
          exact
          component={LawFirmDashboard}
        />
        <Route path="/:lang/app/law-firm" exact component={LawFirmDashboard} />

        {/* Case Routes */}
        <Route path="/:lang/app/law-firm/cases" exact component={CaseList} />
        <Route
          path="/:lang/app/law-firm/cases/new"
          exact
          component={CaseForm}
        />
        <Route
          path="/:lang/app/law-firm/cases/:id/edit"
          exact
          component={CaseForm}
        />
        <Route
          path="/:lang/app/law-firm/cases/:id"
          exact
          component={CaseDetail}
        />
        {/* Time Entry Routes */}
        <Route
          path="/:lang/app/law-firm/time-entries"
          exact
          component={TimeEntryList}
        />
        <Route
          path="/:lang/app/law-firm/time-entries/new"
          exact
          component={TimeEntryForm}
        />
        <Route
          path="/:lang/app/law-firm/time-entries/:entryId/edit"
          exact
          component={TimeEntryForm}
        />
        <Route
          path="/:lang/app/law-firm/cases/:caseId/time-entries"
          exact
          component={TimeEntryList}
        />
        {/* Task Routes */}
        <Route path="/:lang/app/law-firm/tasks" exact component={TaskList} />
        <Route
          path="/:lang/app/law-firm/tasks/new"
          exact
          component={TaskForm}
        />
        <Route
          path="/:lang/app/law-firm/tasks/:taskId/edit"
          exact
          component={TaskForm}
        />
        <Route
          path="/:lang/app/law-firm/cases/:caseId/tasks"
          exact
          component={TaskList}
        />
        {/* Billing Routes */}
        <Route
          path="/:lang/app/law-firm/billing"
          exact
          component={BillingDashboard}
        />
        {/* Expense Routes */}
        <Route
          path="/:lang/app/law-firm/expenses"
          exact
          component={ExpenseList}
        />
        <Route
          path="/:lang/app/law-firm/expenses/new"
          exact
          component={ExpenseForm}
        />
        <Route
          path="/:lang/app/law-firm/expenses/:expenseId/edit"
          exact
          component={ExpenseForm}
        />
        <Route
          path="/:lang/app/law-firm/cases/:caseId/expenses"
          exact
          component={ExpenseList}
        />
        {/* Reports Routes */}
        <Route
          path="/:lang/app/law-firm/reports"
          exact
          component={ReportsDashboard}
        />
        <Route
          path="/:lang/app/law-firm/reports/financial"
          exact
          component={LawFirmFinancialReport}
        />
        <Route
          path="/:lang/app/law-firm/reports/time-utilization"
          exact
          component={TimeUtilizationReport}
        />
        <Route
          path="/:lang/app/law-firm/reports/attorney-performance"
          exact
          component={AttorneyPerformanceReport}
        />
        <Route
          path="/:lang/app/law-firm/reports/client-billing"
          exact
          component={ClientBillingReport}
        />
        <Route
          path="/:lang/app/law-firm/reports/case-profitability"
          exact
          component={CaseProfitabilityReport}
        />
        <Route
          path="/:lang/app/law-firm/reports/practice-areas"
          exact
          component={PracticeAreaReport}
        />
        <Route
          path="/:lang/app/law-firm/reports/realization"
          exact
          component={RealizationRateReport}
        />
        <Route
          path="/:lang/app/law-firm/reports/custom"
          exact
          component={CustomReport}
        />
        {/* Client Management Routes */}
        <Route
          path="/:lang/app/law-firm/clients"
          exact
          component={ClientList}
        />
        {/* Trust Account Routes */}
        <Route
          path="/:lang/app/law-firm/trust-accounts"
          exact
          component={TrustAccountDashboard}
        />
        {/* Document Management Routes */}
        <Route
          path="/:lang/app/law-firm/documents"
          exact
          component={DocumentManagement}
        />
        {/* Court Calendar Routes */}
        <Route
          path="/:lang/app/law-firm/calendar"
          exact
          component={CourtCalendar}
        />
        {/* Conflict Checker Routes */}
        <Route
          path="/:lang/app/law-firm/conflicts"
          exact
          component={ConflictChecker}
        />
        {/* Client Portal Routes */}
        <Route
          path="/:lang/app/law-firm/client-portal"
          exact
          component={LawFirmClientPortal}
        />
        {/* Matter Management Routes */}
        <Route
          path="/:lang/app/law-firm/matters"
          exact
          component={MatterManagement}
        />
        {/* Client Portal Routes - Accessible without /app prefix for public access */}
        <Route path="/:lang/client-login" exact component={ClientLogin} />
        <Route
          path="/:lang/client-portal/home"
          exact
          component={ClientPortalHome}
        />
        <Route
          path="/:lang/client-portal/profile"
          exact
          component={ClientProfile}
        />
        <Route
          path="/:lang/client-portal/law-firm"
          exact
          component={LawFirmClientPortal}
        />
        <Route
          path="/:lang/client-portal/lawfirm"
          exact
          component={LawFirmClientPortal}
        />
        <Route
          path="/:lang/client-portal/construction"
          exact
          component={ConstructionClientPortal}
        />
        <Route
          path="/:lang/client-portal/healthcare"
          exact
          component={HealthcareClientPortal}
        />
        <Route
          path="/:lang/client-portal/accounting"
          exact
          component={AccountingClientPortal}
        />
        <Route
          path="/:lang/client-portal/settings"
          exact
          component={ClientPortalSettings}
        />
        <Route
          path="/:lang/app/notifications"
          exact
          component={NotificationsList}
        />
        <Route path="/:lang/app/letters/add" exact component={Letter} />
        <Route path="/:lang/app/letters/create" exact component={Letter} />
        <Route path="/:lang/app/letters/edit/:id" exact component={Letter} />
        <Route
          path="/:lang/app/letters/view/:id"
          exact
          component={LetterViewer}
        />
        <Route
          path="/:lang/app/letters/templates/library"
          exact
          component={TemplateLibrary}
        />
        <Route
          path="/:lang/app/letters/templates"
          exact
          component={TemplateManager}
        />
        <Route
          path="/:lang/app/letters/approvals/queue"
          exact
          component={ApprovalQueue}
        />
        <Route
          path="/:lang/app/letters/approvals"
          exact
          component={ApprovalDashboard}
        />
        <Route path="/:lang/app/letters/inbox" exact component={LetterInbox} />
        <Route
          path="/:lang/app/letters/outbox"
          exact
          component={LetterOutbox}
        />
        <Route
          path="/:lang/app/letters/search"
          exact
          render={() => <LettersList letterType="search" />}
        />
        <Route
          path="/:lang/app/letters/drafts"
          exact
          render={() => <LettersList letterType="draft" />}
        />
        <Route
          path="/:lang/app/letters/sent"
          exact
          render={() => <LettersList letterType="sent" />}
        />
        <Route path="/:lang/app/letters/home" exact component={LettersHome} />
        <Route path="/:lang/app/letters" exact component={LettersList} />
        <Route path="/:lang/app/costcenters" exact component={Costcenters} />
        <Route path="/:lang/app/trial-balance" exact component={TrialBalance} />
        <Route path="/:lang/app/balance-sheet" exact component={BalanceSheet} />
        <Route
          path="/:lang/app/label/print/:id"
          exact
          component={BarcodePrint}
        />
        <Route path="/:lang/app/qrcode" exact component={QRCodeGenerator} />
        <Route path="/:lang/app/qrcode/create" exact component={QRCodeForm} />
        <Route path="/:lang/app/qrcode/edit/:id" exact component={QRCodeForm} />
        <Route
          path="/:lang/app/income-statement"
          exact
          component={IncomeStatement}
        />
        <Route path="/:lang/app/cash-flow" exact component={CashFlow} />
        <Route
          path="/:lang/app/comprehensive-income"
          exact
          component={ComprehensiveIncome}
        />
        <Route
          path="/:lang/app/stockholders-equity"
          exact
          component={StockholdersEquity}
        />
        <Route
          path="/:lang/app/general-ledger"
          exact
          component={GeneralLedger}
        />
        <Route path="/:lang/app/aging-report" exact component={AgingReport} />
        <Route
          path="/:lang/app/account-statement"
          exact
          component={AccountStatement}
        />
        <Route path="/:lang/app/vat-report" exact component={VATReport} />
        <Route
          path="/:lang/app/project-profitability"
          exact
          component={ProjectProfitability}
        />
        <Route
          path="/:lang/app/purchases/add"
          exact
          component={PurchaseInvoice}
        />
        <Route
          path="/:lang/app/purchases/edit/:id"
          exact
          component={PurchaseInvoice}
        />
        <Route
          path="/:lang/app/purchases/duplicate/:id"
          exact
          component={PurchaseInvoice}
        />
        <Route
          path="/:lang/app/purchases/view/:id"
          exact
          component={PurchaseInvoiceDetails}
        />
        <Route path="/:lang/app/purchases" exact component={PurchaseInvoices} />
        <Route path="/:lang/app/payrolls" exact component={PayrollsHome} />
        <Route path="/:lang/app/payrolls/list" exact component={PayrollList} />
        <Route
          path="/:lang/app/payrolls/list/:page"
          exact
          component={PayrollList}
        />
        <Route path="/:lang/app/assets" exact component={AssetsList} />
        <Route path="/:lang/app/assets/add" exact component={AssetsForm} />
        <Route path="/:lang/app/assets/edit/:id" exact component={AssetsForm} />
        <Route
          path="/:lang/app/assets/view/:id"
          exact
          component={AssetsViewer}
        />
        <Route
          path="/:lang/app/payrolls/generate"
          exact
          component={GeneratePayroll}
        />
        <Route
          path="/:lang/app/payrolls/bulk-generate"
          exact
          component={BulkGeneratePayroll}
        />
        <Route
          path="/:lang/app/payrolls/analytics"
          exact
          component={PayrollAnalytics}
        />
        <Route
          path="/:lang/app/payrolls/payments"
          exact
          component={PayrollPayments}
        />
        <Route path="/:lang/app/attendances" exact component={AttendanceList} />
        <Route
          path="/:lang/app/attendances/record"
          exact
          component={RecordAttendance}
        />
        {/* Modern Attendance Routes */}
        <Route
          path="/:lang/app/attendance/home"
          exact
          component={AttendanceHome}
        />
        <Route
          path="/:lang/app/attendance/checkin"
          exact
          component={ModernCheckInOut}
        />
        <Route
          path="/:lang/app/attendance/records"
          exact
          component={AttendanceList}
        />
        <Route
          path="/:lang/app/attendance/shifts"
          exact
          component={ShiftManagement}
        />
        <Route
          path="/:lang/app/attendance/holidays"
          exact
          component={HolidayManagement}
        />
        <Route
          path="/:lang/app/attendance/reports"
          exact
          component={AttendanceReports}
        />
        <Route
          path="/:lang/app/attendance/employees"
          exact
          component={AttendanceList}
        />
        <Route
          path="/:lang/app/attendance/timetracking"
          exact
          component={TimeTracking}
        />
        <Route
          path="/:lang/app/settings/attendance"
          exact
          component={AttendanceSettings}
        />
        <Route path="/:lang/app/settings/ai" component={AISettings} />
        <Route path="/:lang/app/settings/mfa" component={MFASettings} />
        <Route path="/:lang/app/security-settings" exact component={SecuritySettings} />
        <Route path="/:lang/app/settings/modules" exact component={ModuleSettings} />
        <Route path="/:lang/app/website" component={WebsiteManager} />
        <Route
          path="/:lang/app/profit-centers"
          exact
          component={ProfitCentersList}
        />
        <Route path="/:lang/app/sms-providers" exact component={SMSProvider} />
        <Route path="/:lang/app/coupons" exact component={Coupons} />
        <Route
          path="/:lang/app/coupons/print/:id"
          exact
          component={CreateCoupons}
        />
        <Route
          path="/:lang/app/communication-center"
          exact
          component={CommunicationCenter}
        />
        <Route path="/:lang/app/vouchers" exact component={Vouchers} />
        <Route
          path="/:lang/app/accounting-settings"
          exact
          component={AccountingSettings}
        />
        {/* Clinic Management Routes */}
        <Route
          path="/:lang/app/clinic"
          exact
          component={ClinicManagementHome}
        />
        <Route path="/:lang/app/clinic/doctors" exact component={DoctorList} />
        <Route
          path="/:lang/app/clinic/doctors/add"
          exact
          component={DoctorForm}
        />
        <Route
          path="/:lang/app/clinic/doctors/edit/:id"
          exact
          component={DoctorForm}
        />
        <Route
          path="/:lang/app/clinic/doctors/:id"
          exact
          component={DoctorDetail}
        />
        <Route
          path="/:lang/app/clinic/patients"
          exact
          component={PatientList}
        />
        <Route
          path="/:lang/app/clinic/patients/add"
          exact
          component={PatientForm}
        />
        <Route
          path="/:lang/app/clinic/patients/edit/:id"
          exact
          component={PatientForm}
        />
        <Route
          path="/:lang/app/clinic/patients/:id"
          exact
          component={PatientDetail}
        />
        <Route
          path="/:lang/app/clinic/appointments"
          exact
          component={AppointmentList}
        />
        <Route
          path="/:lang/app/clinic/appointments/add"
          exact
          component={AppointmentForm}
        />
        <Route
          path="/:lang/app/clinic/appointments/edit/:id"
          exact
          component={AppointmentForm}
        />
        <Route
          path="/:lang/app/clinic/appointments/view/:id"
          exact
          component={AppointmentDetail}
        />
        <Route
          path="/:lang/app/clinic/appointments/:id"
          exact
          component={AppointmentDetail}
        />
        <Route
          path="/:lang/app/clinic/lab-results"
          exact
          component={LabResultList}
        />
        <Route
          path="/:lang/app/clinic/lab-results/:id"
          exact
          component={LabResultDetail}
        />
        <Route
          path="/:lang/app/clinic/lab-results/add"
          exact
          component={LabResultForm}
        />
        <Route
          path="/:lang/app/clinic/lab-results/edit/:id"
          exact
          component={LabResultForm}
        />
        <Route
          path="/:lang/app/clinic/lab-tests"
          exact
          component={LabTestList}
        />
        <Route
          path="/:lang/app/clinic/lab-tests/add"
          exact
          component={LabTestForm}
        />
        <Route
          path="/:lang/app/clinic/lab-tests/edit/:id"
          exact
          component={LabTestForm}
        />
        <Route
          path="/:lang/app/clinic/lab-tests/:id"
          exact
          component={LabTestDetail}
        />
        <Route
          path="/:lang/app/clinic/medical-records"
          exact
          component={MedicalRecordList}
        />
        <Route
          path="/:lang/app/clinic/medical-records/add"
          exact
          component={MedicalRecordForm}
        />
        <Route
          path="/:lang/app/clinic/medical-records/edit/:id"
          exact
          component={MedicalRecordForm}
        />
        <Route
          path="/:lang/app/clinic/medical-records/:id"
          exact
          component={MedicalRecordDetail}
        />
        <Route
          path="/:lang/app/clinic/prescriptions"
          exact
          component={PrescriptionList}
        />
        <Route
          path="/:lang/app/clinic/prescriptions/add"
          exact
          component={PrescriptionForm}
        />
        <Route
          path="/:lang/app/clinic/prescriptions/edit/:id"
          exact
          component={PrescriptionForm}
        />
        <Route
          path="/:lang/app/clinic/prescriptions/:id"
          exact
          component={PrescriptionDetail}
        />
        <Route
          path="/:lang/app/clinic/medications"
          exact
          component={MedicationList}
        />
        <Route
          path="/:lang/app/clinic/medications/add"
          exact
          component={MedicationForm}
        />
        <Route
          path="/:lang/app/clinic/medications/edit/:id"
          exact
          component={MedicationForm}
        />
        <Route
          path="/:lang/app/clinic/medications/:id"
          exact
          component={MedicationDetail}
        />
        <Route path="/:lang/app/messaging" exact component={Messaging} />
        <Route path="/:lang/app/payments" exact component={PaymentsList} />
        {/* Inventory Orders */}
        <Route
          path="/:lang/app/inventory-orders"
          exact
          component={InventoryOrders}
        />
        {/* Credit / Debit Notes */}
        <Route
          path="/:lang/app/credit-debit-notes"
          exact
          component={CreditDebitNoteList}
        />
        <Route
          path="/:lang/app/credit-debit-notes/new"
          exact
          component={AddCreditDebitNote}
        />
        <Route
          path="/:lang/app/credit-debit-notes/:id/edit"
          exact
          component={AddCreditDebitNote}
        />
        <Route
          path="/:lang/app/credit-debit-notes/:id"
          exact
          component={CreditDebitNoteDetails}
        />
        {/* Workflow Routes */}
        <Route path="/:lang/app/workflow-settings" exact component={WorkflowSettings} />
        <Route path="/:lang/app/workflow-inbox" exact component={WorkflowInbox} />
        {/* To-Do App Routes */}
        <Route path="/:lang/app/todos" exact component={TodoApp} />
        {/* Subscription Management Routes */}
        <Route
          path="/:lang/app/my-subscription"
          exact
          component={MySubscription}
        />
        <Route path="/:lang/app/my-billing" exact component={MyBilling} />
      </Switch>
    </Suspense>
  );
}
